import React, { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

/**
 * Chips de números inteiros (dias) com botão de adicionar e remover.
 * value: array de inteiros
 * onChange: (newArray) => void
 */
const DayChipsInput = ({ value = [], onChange, max = 365, accent = 'blue', testIdPrefix = 'day-chip' }) => {
  const [draft, setDraft] = useState('');

  const colorMap = {
    blue: { bg: 'bg-blue-50', text: 'text-[#003366]', border: 'border-blue-200', hover: 'hover:bg-blue-100' },
    amber: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', hover: 'hover:bg-amber-100' },
    rose: { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200', hover: 'hover:bg-rose-100' },
  };
  const c = colorMap[accent] || colorMap.blue;

  const addDay = () => {
    const n = parseInt(draft, 10);
    if (Number.isNaN(n) || n < 0 || n > max) {
      setDraft('');
      return;
    }
    if (!value.includes(n)) {
      const next = [...value, n].sort((a, b) => b - a);
      onChange(next);
    }
    setDraft('');
  };

  const removeDay = (n) => onChange(value.filter(v => v !== n));

  const handleKey = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addDay();
    }
  };

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-container`}>
      <div className="flex flex-wrap gap-1.5 min-h-[34px] p-1.5 border border-gray-200 rounded-md bg-white">
        {value.length === 0 ? (
          <span className="text-xs text-gray-400 px-1">Nenhum dia configurado</span>
        ) : (
          value.map((n) => (
            <Badge
              key={n}
              variant="outline"
              className={`${c.bg} ${c.text} ${c.border} flex items-center gap-1 px-2 py-0.5`}
              data-testid={`${testIdPrefix}-${n}`}
            >
              {n} dia{n === 1 ? '' : 's'}
              <button
                type="button"
                onClick={() => removeDay(n)}
                className={`${c.hover} rounded-full p-0.5`}
                aria-label={`Remover ${n} dias`}
                data-testid={`${testIdPrefix}-remove-${n}`}
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))
        )}
      </div>
      <div className="flex items-center gap-2">
        <Input
          type="number"
          min={0}
          max={max}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKey}
          placeholder="Ex: 30"
          className="w-32 bg-white border-gray-300 text-gray-900 text-sm"
          data-testid={`${testIdPrefix}-input`}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addDay}
          className="bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
          data-testid={`${testIdPrefix}-add-btn`}
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Adicionar
        </Button>
      </div>
    </div>
  );
};

export default DayChipsInput;
